// src/components/CatechistQRScanner.js
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { StudentContext } from '../StudentContext';
import { recordCatechistScanAll, fetchDateLocks, subscribeMeetingDates } from '../FirebaseService';
import { auth } from '../firebaseConfig';
import qrScanIcon from './access/qr-code-scan.svg';
import './CatechistQRScanner.css';

const CatechistQRScanner = () => {
  const { currentYearId, catechistsData } = useContext(StudentContext);

  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [isTodayLocked, setIsTodayLocked] = useState(false);
  const [hasMeetingToday, setHasMeetingToday] = useState(false);

  const [scanResults, setScanResults] = useState(() => {
    const saved = sessionStorage.getItem('catechistScanResults');
    return saved ? JSON.parse(saved).map(item => ({
      ...item,
      timestamp: new Date(item.timestamp)
    })) : [];
  });

  const scannerRef = useRef(null);
  const currentUser = auth.currentUser;
  const currentUid = currentUser ? currentUser.uid : '';
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  // 1. Kiểm tra quyền quét của người dùng hiện tại
  const currentCatechist = useMemo(() => {
    if (!catechistsData || !currentUid) return null;
    return catechistsData.find(c => c.uid === currentUid);
  }, [catechistsData, currentUid]);

  const hasScannerPermission = useMemo(() => {
    const role = localStorage.getItem('userRole') || currentCatechist?.role;
    if (role === 'admin') return true;

    const rawDept = currentCatechist?.departmentLeader || localStorage.getItem('departmentLeader') || '';
    let depts = rawDept;
    try {
      if (typeof rawDept === 'string' && (rawDept.startsWith('[') || rawDept.startsWith('{'))) {
        depts = JSON.parse(rawDept);
      }
    } catch (e) {}
    const isDiscipline = Array.isArray(depts)
      ? depts.includes('Ban Kỷ Luật')
      : typeof depts === 'string' && depts.split(',').map(s => s.trim()).includes('Ban Kỷ Luật');

    if (isDiscipline) return true;

    return Boolean(currentCatechist?.catechistAttendanceAccess);
  }, [currentCatechist]);

  // 2. Lấy trạng thái khóa ngày & lịch họp của hôm nay
  useEffect(() => {
    if (!currentYearId) return;

    fetchDateLocks().then(locks => {
      setIsTodayLocked(Boolean(locks?.[todayStr]));
    });

    const unsubscribe = subscribeMeetingDates(currentYearId, (datesMap) => {
      setHasMeetingToday(Boolean(datesMap?.[todayStr]));
    });

    return () => unsubscribe && unsubscribe();
  }, [currentYearId, todayStr]);

  useEffect(() => {
    sessionStorage.setItem('catechistScanResults', JSON.stringify(scanResults));
  }, [scanResults]);

  const catechistMaps = useMemo(() => {
    const byCode = {};
    const byUid = {};

    if (catechistsData && catechistsData.length > 0) {
      catechistsData.forEach((c) => {
        if (c.idCode) byCode[c.idCode.trim().toLowerCase()] = c;
        if (c.uid) byUid[c.uid] = c;
      });
    }

    return { byCode, byUid };
  }, [catechistsData]);

  // Khởi tạo camera
  useEffect(() => {
    if (scanning && !scannerRef.current) {
      setTimeout(() => {
        try {
          scannerRef.current = new Html5QrcodeScanner(
            'catechist-reader',
            {
              fps: 10,
              qrbox: { width: 220, height: 220 },
              facingMode: 'environment',
              showTorchButton: true,
              showZoomSlider: true,
            },
            false
          );
          scannerRef.current.render(onScanSuccess, () => {});
        } catch (error) {
          console.error('Lỗi scanner camera:', error);
          setScanning(false);
        }
      }, 100);
    } else if (!scanning && scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
      }
    };
  }, [scanning]);

  const addScanResult = (result) => {
    setScanResults((prev) => {
      if (result.status !== 'processing') {
        const updated = prev.map(item =>
          item.code === result.code && item.status === 'processing' ? result : item
        );
        if (!updated.some(item => item.code === result.code)) {
          return [result, ...updated].slice(0, 15);
        }
        return updated.slice(0, 15);
      } else {
        return [result, ...prev].slice(0, 15);
      }
    });
  };

  const processAttendance = async (targetCodeOrUid) => {
    if (!targetCodeOrUid || !targetCodeOrUid.trim()) return;

    if (isTodayLocked) {
      alert('⛔ Ngày hôm nay đã được KHÓA điểm danh. Không thể ghi nhận thêm!');
      return;
    }

    const now = new Date();
    const inputClean = targetCodeOrUid.trim().toLowerCase();
    const targetCatechist =
      catechistMaps.byCode[inputClean] ||
      catechistMaps.byUid[targetCodeOrUid.trim()];

    if (!targetCatechist) {
      addScanResult({
        code: targetCodeOrUid,
        name: 'Mã GLV không tồn tại',
        timestamp: now,
        status: 'error'
      });
      return;
    }

    if (targetCatechist.uid === currentUid) {
      addScanResult({
        code: targetCatechist.idCode || targetCatechist.uid,
        name: `${targetCatechist.fullName} (Không được tự quét)`,
        timestamp: now,
        status: 'error'
      });
      alert('⛔ BẠN KHÔNG THỂ TỰ ĐIỂM DANH CHO CHÍNH MÌNH! Vui lòng nhờ người trực khác quét mã.');
      return;
    }

    const displayName = `${targetCatechist.holyName || ''} ${targetCatechist.fullName}`.trim();
    addScanResult({
      code: targetCatechist.idCode || targetCatechist.uid,
      name: displayName,
      timestamp: now,
      status: 'processing'
    });

    try {
      const timeStr = now.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });

      const res = await recordCatechistScanAll(
        currentYearId,
        targetCatechist.uid,
        timeStr,
        currentUid
      );

      if (res.success) {
        const extraNote = res.hasMeetingToday ? '(Lễ, Dạy, Họp)' : '(Lễ & Dạy)';
        addScanResult({
          code: targetCatechist.idCode || targetCatechist.uid,
          name: `${displayName} - ${extraNote} (Có lúc ${timeStr})`,
          timestamp: now,
          status: 'success'
        });
        setManualCode('');
      } else {
        addScanResult({
          code: targetCatechist.idCode || targetCatechist.uid,
          name: res.message || 'Lỗi ghi dữ liệu',
          timestamp: now,
          status: 'error'
        });
      }
    } catch (err) {
      console.error('Lỗi khi điểm danh GLV:', err);
      addScanResult({
        code: targetCatechist.idCode || targetCatechist.uid,
        name: 'Lỗi hệ thống: ' + err.message,
        timestamp: now,
        status: 'error'
      });
    }
  };

  const onScanSuccess = (decodedText) => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }
    setScanning(false);
    processAttendance(decodedText);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    processAttendance(manualCode);
  };

  // Chặn giao diện nếu không có quyền
  if (!hasScannerPermission) {
    return (
      <div className="cqr-scan-container" style={{ padding: '40px 16px', textAlign: 'center' }}>
        <h3>⛔ Không có quyền truy cập</h3>
        <p style={{ color: '#64748b' }}>Bạn chưa được phân quyền thực hiện điểm danh Giáo Lý Viên.</p>
      </div>
    );
  }

  return (
    <div className="cqr-scan-container">
      {/* Banner Cảnh Báo Khóa Ngày */}
      {isTodayLocked && (
        <div style={{
          width: '100%',
          backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5',
          color: '#b91c1c',
          padding: '10px 16px',
          borderRadius: '10px',
          marginBottom: '12px',
          fontSize: '13px',
          boxSizing: 'border-box'
        }}>
          🔒 <strong>ĐIỂM DANH HÔM NAY ĐÃ BỊ KHÓA:</strong> Hệ thống hiện không nhận thêm lượt quét mới.
        </div>
      )}

      {/* Badge Tiêu Đề */}
      <div className="cqr-compact-badge">
        <span className="cqr-badge-role">🪪 ĐIỂM DANH GIÁO LÝ VIÊN</span>
        <span className="cqr-badge-hint">
          {hasMeetingToday 
            ? '📌 Ghi nhận: Đi Lễ, Dạy Học & Đi Họp (Hôm nay có lịch họp)' 
            : '📌 Ghi nhận: Đi Lễ & Dạy Học (Hôm nay không có họp)'}
        </span>
      </div>

      {/* Ô Nhập Thủ Công */}
      <form onSubmit={handleManualSubmit} className="cqr-manual-form">
        <input
          type="text"
          placeholder="Nhập ID Code hoặc UID (Ví dụ: GLV001)..."
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          disabled={isTodayLocked}
          className="cqr-manual-input"
        />
        <button type="submit" disabled={isTodayLocked} className="cqr-manual-btn">
          Xác Nhận
        </button>
      </form>

      {/* Bảng Nhật Ký */}
      <div className="cqr-results-table">
        <table>
          <thead>
            <tr>
              <th>Thời gian</th>
              <th>Mã ID</th>
              <th>Giáo lý viên</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {scanResults.length > 0 ? (
              scanResults.map((result, index) => (
                <tr key={index} className={result.status}>
                  <td>{result.timestamp.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</td>
                  <td className="cqr-td-code">{result.code}</td>
                  <td className="cqr-td-name">{result.name}</td>
                  <td className="cqr-td-status">
                    {result.status === 'processing' && <div className="cqr-spinner"></div>}
                    {result.status === 'success' && <span className="cqr-icon-success">✓</span>}
                    {result.status === 'error' && <span className="cqr-icon-error">✕</span>}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="cqr-empty-msg">
                  Chưa có nhật ký điểm danh nào trong phiên này.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Nút Quét Mã Camera */}
      <button
        className="cqr-fab-btn"
        onClick={() => setScanning(true)}
        disabled={isTodayLocked}
        style={isTodayLocked ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
      >
        <img src={qrScanIcon} alt="Scan QR" className="cqr-fab-icon" />
        <span className="cqr-fab-text">Quét mã QR</span>
      </button>

      {/* Modal Camera */}
      {scanning && (
        <div className="cqr-modal-overlay">
          <div className="cqr-modal-card">
            <div className="cqr-modal-header">
              <div className="cqr-title-box">
                <img src={qrScanIcon} alt="Scan" className="cqr-header-icon" />
                <h4>Bỏ Mã QR Vào Khung Quét</h4>
              </div>
              <button className="cqr-close-btn" onClick={() => setScanning(false)}>✕</button>
            </div>
            <div className="cqr-modal-body">
              <div id="catechist-reader"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatechistQRScanner;